/**
 ** module: SLTNeighborDirectory.js
 ** usage: main UI for displaying the POA directory information
 ** created: 12/1/2025
 ** owner: Rob Blanch
 **/
import React from 'react';
import {withRouter} from 'react-router-dom';
import SLTHeaderNavBar from './SLTHeaderNavBar.js';
import FormMessage from './FormMessage.js';
import SLTSnowAppErrHelper from './SLTSnowAppErrHelper.js';
import SLTDirectoryPage from './SLTDirectoryPage.js';

/**
 * class: SLT_NeighborDirectory
 * usage: used to display neighborhood directory information
 * props:
 *      isLoggedIn - (mandatory) true/false flag indicating if user is logged in
 *      logoItem - logo information to display in nav header of form
 *      isNavBarExpanded - (mandatory) true/false flag indicating if nav bar menu should be expanded
 *      setNavBarExpanded(newExpandState) - (mandatory) callback to alter state of isNavBarExpanaded to newExpandState value
 *      getLotOwners(onComplete, onError) - (mandatory) callback routine to get owner POA data from system
 *      postLotOwners(onComplete, onError, updatedLots) - (optional) callback routine to post updated owner POA data into system
 *      getDisplayRowIndex(tablename) - (mandatory) used to obtain the current index within a table
 *      setDisplayRowIndex(tablename, rowIndex) - (mandatory) used to set the current index within a table presented on tab
 *      getLotsAsGeoJSON(onComplete, onError) - (mandatory) callback to get lots as geoJSON for map overlay
 *      onGetUserPasskeys(onComplete, onError) - (optional) callback to retrieve the user's passkeys
 *      onCreatePasskey(onComplete, onError) - (optional) callback to handle creating a new passkey
 *      onDeletePasskey(credential_id, onComplete, onError) - (optional) callback to handle deleting a passkey
*/
class SLTNeighborDirectory extends React.Component {
    constructor(props) {
        var msg;
        var err;

        super(props);

        this.state = {
            isMounted: false,
            error: null,
            message: null,
            user_key: null,
            lots: null,
            navItems: [
                {link:'/logout',label:'Sign Out',icon:null}
            ]
        };

        this.handleChange = this.handleInputChange.bind(this);
        this.handleLoadData = this.handleLoadData.bind(this);

        if (this.props.isLoggedIn === undefined || this.props.isLoggedIn === null) {
            msg = 'SLTSNW_Directory: isLoggedIn prop not provided, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
            return;
        }

        if (this.props.isLoggedIn === false) {
            msg = 'SLTSNW_Directory: user not logged in, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
            return;
        }

        if (this.props.getLotOwners === undefined || this.props.getLotOwners === null) {
            msg = 'SLTSNW_Directory: getLotOwners prop not provided, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
        }

        if (this.props.getDisplayRowIndex === undefined || this.props.getDisplayRowIndex === null) {
            msg = 'SLTSNW_Directory: getLotOwners prop not provided, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
        }

        if (this.props.setDisplayRowIndex === undefined || this.props.setDisplayRowIndex === null) {
            msg = 'SLTSNW_Directory: setDisplayRowIndex prop not provided, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
        }

        if (this.props.getLotsAsGeoJSON === undefined || this.props.getLotsAsGeoJSON === null) {
            msg = 'SLTSNW_Directory: getLotsAsGeoJSON prop not provided, cannot continue';
            err = new Error(msg);
            this.state = {error: err, message: msg};
        }
    }

    /**
     * handleLoadData
     *      load owner data from system
     */
    handleLoadData(event) {
        if (this.props.getLotOwners === undefined || this.props.getLotOwners === null) {
            return;
        }

        this.props.getLotOwners(
            (lots) => {
                if (this.state.isMounted === false) {
                    return;
                }

                if (lots === undefined || lots === null) {
                    this.setState({message: 'No neighborhood directory data found', lots: []});
                    return;
                }

                this.setState({lots: lots.lots});
            },
            (error) => {
                var appErrHelper = new SLTSnowAppErrHelper();
                const errMsg = appErrHelper.getUserErrMsgFromError(error);
                if (errMsg !== null && errMsg !== undefined && errMsg !== '' && errMsg.includes('getSessionID()')) {
                    // session appears to be invalid - redirect to login page
                    if (this.props.onSessionInvalid) {
                        this.props.onSessionInvalid(() => {
                            this.props.history.replace('/login');
                        });
                    } else {
                        this.props.history.replace('/login');
                    }
                    return;
                }

                if (this.state.isMounted === false) {
                    return;
                }

                this.setState({message: errMsg, error: error});
            }
        );
    }

    /**
     * getDirectoryData
     *      routine to obtain directory data for table (note it relies on state data)
     */
    getDirectoryData(onComplete, onError) {
        const lots = this.state.lots;

        if (lots !== null) {
            onComplete(lots);
        } else {
            onError(new Error('Directory data not loaded'));
        }
    }

    /**
     * setSelectedNeighbor
     *      routine called when user selects a neighbor row in the directory table
     */
    setSelectedNeighbor(rowData) {
        // currently no action on select
    }

    /**
     * componentDidMount
     *      react js routine called right after component mounts
     */
    componentDidMount()
    {
        if (this.state.isMounted === false) {
            this.setState({isMounted: true});
        }

        if (this.props.isLoggedIn === false) {
            this.props.history.replace('/login');
            return;
        }
        this.handleLoadData();
    }

    /**
     * handleInputChange
     *      routine to maintain state relative to user edits in UI
     */
    handleInputChange(event) {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        this.setState({
            [name]: value
        });
    }

    /**
     * addHeader
     *  return title for table
     */
    addHeader(title) {
        return (
            <div className="ProfileHeader">
                <div className="ProfileHeaderLabel">
                    {(title) ? title : 'Neighborhood Directory'}
                </div>
            </div>
        );
    }

    /**
     * renderLoadingMessage
     *  show message indicating the owner information is being loaded from service
     */
    renderLoadingMessage() {
        const message = this.state.message;

        return(
            <table className="Page_Table" name="NeighborhoodDirectoryTable" align="center">
            <tbody>
                <tr>
                    <td>
                        <FormMessage error={this.state.error} message={message} />
                    </td>
                </tr>
                <tr>
                    <td>
                        {this.addHeader()}
                    </td>
                </tr>

                <tr colSpan="1" align="center">
                    <td className="Page_Col">
                        Loading neighborhood directory...
                    </td>
                </tr>
            </tbody>
            </table>);
    }

    /**
     * setNavItems
     *    routine called to set navigation items in state
     */
    setNavItems(navItems = null) {
        if (navItems === undefined || navItems === null || Array.isArray(navItems) === false || navItems.length === 0) {
            this.setState({navItems: [{link:'/logout',label:'Sign Out',icon:null}]});
        } else {
            this.setState({navItems: navItems});
        }
    }

    /**
     * updateLot
     *     routine called to update lot information in directory
     */
    updateLot(updatedLot) {
        var lots = this.state.lots;
        var lotIndex = lots.findIndex((lot) => (lot.neighborhood === updatedLot.neighborhood && lot.lot_number === updatedLot.lot_number));

        if (lotIndex !== -1 && lots[lotIndex] !== undefined && lots[lotIndex] !== null &&
            this.props.postLotOwners !== undefined && this.props.postLotOwners !== null) {
            this.props.postLotOwners(
                (response) => {
                    // update local state
                    lots[lotIndex] = updatedLot;
                    this.setState({lots: lots});
                    console.log('Successfully updated lot information');
                },
                (error) => {
                    var appErrHelper = new SLTSnowAppErrHelper();
                    const errMsg = appErrHelper.getUserErrMsgFromError(error);
                    this.setState({message: errMsg, error: error});
                },
                [updatedLot]
            );                
        }
    }

    /**
     * renderLotDirectoryTable
     *      render table of neighborhood directory for the user
     */
    renderLotDirectoryTable() {
        return (<SLTDirectoryPage 
                    props 
                    lots={this.state.lots} 
                    setNavItems={this.setNavItems.bind(this)}
                    updateLot={(this.props.postLotOwners !== undefined && this.props.postLotOwners !== null) ? this.updateLot.bind(this) : null}
                    getLotsAsGeoJSON={this.props.getLotsAsGeoJSON}
                    onGetUserPasskeys={this.props.onGetUserPasskeys}
                    onCreatePasskey={(this.props.onCreatePasskey !== undefined && this.props.onCreatePasskey !== null) ? this.props.onCreatePasskey : null}
                    onDeletePasskey={(this.props.onDeletePasskey !== undefined && this.props.onDeletePasskey !== null) ? this.props.onDeletePasskey : null}
                     />);
    }

    /**
     * componentWillUnmount
     *      react js routine called right before component unmounts
     */
    componentWillUnmount() {
        if(this.state.isMounted === false) {
            return;
        }

        this.setState({isMounted: false});
    }

    /**
     * render
     *      routine called by react js to layout UI
     */
    render() {
        return(
            <React.StrictMode>
            <SLTHeaderNavBar    props
                                logoItem={this.props.logoItem}
                                navItems={this.state.navItems}
                                isNavBarExpanded={this.props.isNavBarExpanded}
                                setNavBarExpanded={this.props.setNavBarExpanded} />
                                
                {<FormMessage error={this.state.error} message={this.state.message} />}

                {(this.state.lots === null) ?
                    this.renderLoadingMessage() :
                    this.renderLotDirectoryTable()
                }
            </React.StrictMode>
        );
    }
}  

export default withRouter(SLTNeighborDirectory);
